import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Lock,
  Unlock,
  Copy,
  Check,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Brain,
  Target,
  TrendingUp,
  Eye,
  EyeOff,
  Shield,
  Zap
} from 'lucide-react';

/**
 * HP.3 Gated Output: Capability Insight Display
 * Shows the AI-generated capability map with gated sections.
 * Visible: Top 3 capabilities with evidence previews
 * Gated: Full map, blind spot analysis, growth trajectory, evidence chains
 */

const CAPABILITY_ICONS = {
  0: Brain,
  1: Target,
  2: TrendingUp,
  3: Zap,
  4: Shield,
};

const GatedInsightDisplay = ({ data, onUnlock, onStartFresh, accentColor = '#f59e0b' }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [expandedCapabilities, setExpandedCapabilities] = useState({});
  const contentRef = useRef(null);

  if (!data) return null;

  const {
    review_id,
    input_hash,
    summary,
    achievements = [],
    development_areas = {},
    goals = {},
  } = data;

  const visibleCapabilities = achievements.filter(a => a.visible);
  const gatedCapabilities = achievements.filter(a => !a.visible);
  const gatedDevAreas = development_areas.gated_count || 0;
  const gatedGoals = goals.gated_count || 0;
  const totalGated = gatedCapabilities.length + gatedDevAreas + gatedGoals;

  const toggleCapability = (index) => {
    setExpandedCapabilities(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleCopy = () => {
    const text = [
      'CAPABILITY INSIGHT',
      '',
      summary,
      '',
      'TOP CAPABILITIES:',
      ...visibleCapabilities.map((c, i) => `${i + 1}. ${c.title}\n   ${c.detail}`),
    ].join('\n');

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleUnlock = () => {
    const unlockData = {
      review_id,
      input_hash,
      flow_type: 'insight',
    };
    sessionStorage.setItem('rv_pending_unlock', JSON.stringify(unlockData));
    navigate('/login');
  };

  return (
    <div data-testid="insight-gated-display" className="max-w-3xl mx-auto" ref={contentRef}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accentColor}20` }}>
            <Brain className="w-4 h-4" style={{ color: accentColor }} />
          </div>
          <h2 className="text-xl font-bold text-white">Your Capability Profile</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="insight-copy-btn"
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="text-gray-400 hover:text-white"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            <span className="ml-1.5 text-xs">{copied ? 'Copied' : 'Copy'}</span>
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div
        data-testid="insight-summary"
        className="p-5 rounded-xl border mb-6"
        style={{ borderColor: `${accentColor}30`, backgroundColor: `${accentColor}08` }}
      >
        <p className="text-gray-200 leading-relaxed text-sm">{summary}</p>
      </div>

      {/* Visible Capabilities */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Eye className="w-4 h-4" style={{ color: accentColor }} />
          <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Top Capabilities (Evidence-Based)</h3>
        </div>
        <div className="space-y-3">
          {visibleCapabilities.map((cap, i) => {
            const Icon = CAPABILITY_ICONS[i] || Brain;
            const isExpanded = expandedCapabilities[i];
            return (
              <div
                key={i}
                data-testid={`insight-capability-${i}`}
                className="p-4 bg-gray-800/40 border border-gray-700/50 rounded-lg cursor-pointer hover:border-gray-600 transition-colors"
                onClick={() => toggleCapability(i)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center mt-0.5" style={{ backgroundColor: `${accentColor}15` }}>
                      <Icon className="w-4 h-4" style={{ color: accentColor }} />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium text-sm">{cap.title}</h4>
                      {isExpanded && (
                        <p className="text-gray-400 text-sm mt-2 leading-relaxed">{cap.detail}</p>
                      )}
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0 mt-1" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0 mt-1" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Gated Section */}
      {totalGated > 0 && (
        <div
          data-testid="insight-gated-section"
          className="relative p-6 rounded-xl border border-dashed mb-6"
          style={{ borderColor: `${accentColor}40` }}
        >
          {/* Blur overlay */}
          <div className="absolute inset-0 rounded-xl backdrop-blur-sm bg-gray-900/60 z-10 flex flex-col items-center justify-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${accentColor}20` }}>
              <Lock className="w-5 h-5" style={{ color: accentColor }} />
            </div>
            <p className="text-white font-semibold text-sm mb-1">
              {totalGated} more insight{totalGated !== 1 ? 's' : ''} available
            </p>
            <p className="text-gray-400 text-xs mb-4 text-center max-w-xs">
              Blind spot analysis, growth trajectory, and full evidence chains
            </p>
            <Button
              data-testid="insight-unlock-btn"
              onClick={handleUnlock}
              className="text-white font-medium px-6"
              style={{ backgroundColor: accentColor }}
            >
              <Unlock className="w-4 h-4 mr-2" />
              Unlock Full Profile — Free
            </Button>
          </div>

          {/* Blurred preview content */}
          <div className="opacity-30 select-none">
            <div className="flex items-center gap-2 mb-3">
              <EyeOff className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Deeper Insights</h3>
            </div>
            <div className="space-y-3">
              {gatedCapabilities.map((cap, i) => (
                <div key={i} className="p-3 bg-gray-800/30 rounded-lg">
                  <div className="h-4 bg-gray-700/50 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-700/30 rounded w-full" />
                  <div className="h-3 bg-gray-700/30 rounded w-5/6 mt-1" />
                </div>
              ))}
              {gatedDevAreas > 0 && (
                <div className="p-3 bg-gray-800/30 rounded-lg">
                  <div className="h-4 bg-gray-700/50 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-gray-700/30 rounded w-full" />
                </div>
              )}
              {gatedGoals > 0 && (
                <div className="p-3 bg-gray-800/30 rounded-lg">
                  <div className="h-4 bg-gray-700/50 rounded w-2/3 mb-2" />
                  <div className="h-3 bg-gray-700/30 rounded w-full" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-800">
        <Button
          data-testid="insight-start-fresh-btn"
          variant="ghost"
          size="sm"
          onClick={onStartFresh}
          className="text-gray-400 hover:text-white"
        >
          <RefreshCw className="w-4 h-4 mr-1.5" /> Start Fresh
        </Button>
        <p className="text-xs text-gray-500">
          Generated by AI — your data is not stored unless you sign up
        </p>
      </div>
    </div>
  );
};

export default GatedInsightDisplay;
